import React from "react";
import { RouteObject } from "react-router-dom";
import { AuthProvider } from "../auth/AuthProvider";
import { Dashboard } from "./pages/Dashboard";
import { DashboardV2 } from "./pages/DashboardV2";
import { Settings } from "./pages/Settings";
import { BusinessAdminGuard } from "./components/BusinessAdminGuard/BusinessAdminGuard";
import { DashboardLayout } from "./components/DashboardLayout/DashboardLayout";
import { IngredientBuilder } from "./pages/IngredientBuilder/IngredientBuilder";
import { ConsumableBuilder } from "./pages/ConsumableBuilder/ConsumableBuilder";
import { Recipes } from "./pages/Kitchen/Recipes/Recipes";
import { SaleItems } from "./pages/Menu/POSSellableItems/SaleItems";
import { OffersManager } from "./pages/Menu/OffersManager";
import { Ingredients } from "./pages/Inventory/Ingredients";
import { OfferBuilder } from "./pages/Menu/OfferBuilder/OfferBuilder";
import { Consumables } from "./pages/Inventory/Consumables";
import { Suppliers } from "./pages/Suppliers/Suppliers";
import { BranchesManager } from "./pages/Facilities/BranchesManager/BranchesManager";
import { WarehousesManager } from "./pages/Facilities/WarehousesManager/WarehousesManager";
import { StaffManager } from "./pages/Staff/StaffManager";
import { StaffBuilder } from "./pages/Staff/StaffBuilder";
import { RolesManager } from "./pages/Configuration/RolesManager";
import { RoleBuilder } from "./pages/Configuration/RoleBuilder";
import { DepartmentsManager } from "./pages/Facilities/DepartmentsManager/DepartmentsManager";
import { IngredientBatches } from "./pages/Inventory/IngredientBatches";
import { KitchenWaste } from "./pages/Kitchen/KitchenWaste/KitchenWaste";
import { RecipeBuilder } from "./pages/Kitchen/Recipes/RecipeBuilder";
import { ProcessedItemsManager } from "./pages/Kitchen/ProcessedItems/ProcessedItemsManager";
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { BOMManager } from "./pages/Menu/BOM/BOMManager";
import { BOMForm } from "./pages/Menu/BOM/BOMForm";
import { MenuItemsManager } from "./pages/Menu/MenuItems/MenuItemsManager";
import { MenuItemForm } from "./pages/Menu/MenuItems/MenuItemForm";
import { OrdersManager } from "./pages/Orders/OrdersManager";
import { InventoryDashboard } from "./pages/InventoryDashboard";
import { PurchaseRequests } from "./pages/PurchaseRequest";
import { RFQList } from "./pages/RFQ";
import { POList } from "./pages/PO/POList";
import { POSConfiguration } from "./pages/POSConfiguration/POSConfiguration";
import { StockAdjustmentPage } from "./pages/StockAdjustment";
import { TablesConfiguration } from "./pages/TablesConfiguration/TablesConfiguration";
import { GRNList, GRNFormPage } from "./pages/GRN";
import { InvoiceList } from "./pages/Invoices/InvoiceList";
import InvoiceFormPage from "./pages/Invoices/InvoiceFormPage";
import { StockTransferPage } from "./pages/StockTransfer";
import { AppLauncherPage, ProfilePage } from "./AppLauncher";
import { AuditLogsPage } from "./pages/AuditLogs/AuditLogsPage";
import { UserManagementPage } from "./pages/UserManagement/UserManagementPage";
import StockMovementLogs from "./pages/StockMovementLogs/StockMovementLogs";
import StockValuation from "./pages/StockValuation/StockValuation";
import ExpiryReport from "./pages/ExpiryReport/ExpiryReport";

// Wrap the entire authenticated section with AuthProvider
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <AuthProvider>{children}</AuthProvider>;
};

export interface ExtendedRouteObject extends Omit<RouteObject, "children"> {
  id?: string;
  icon?: string;
  permission?: string; // Single permission required to access this route
  children?: ExtendedRouteObject[];
  handle?: { hideSidebar?: boolean };
}

export const businessAdminRoutes: ExtendedRouteObject[] = [
  {
    path: "business",
    element: (
      <AuthenticatedLayout>
        <BusinessAdminGuard>
          <DashboardLayout />
        </BusinessAdminGuard>
      </AuthenticatedLayout>
    ),
    children: [
      // {
      //   index: true,
      //   element: <Dashboard />,
      //   icon: "dashboard",
      // },
      {
        index: true,
        element: <DashboardV2 />,
        icon: "insights",
        permission: "business.dashboard.view",
      },
      // {
      //   id: "inventory-dashboard",
      //   path: "inventory-dashboard",
      //   element: <InventoryDashboard />,
      //   icon: "analytics",
      //   permission: "business.inventory.dashboard.view",
      // },
      {
        id: "stock-management",
        path: "stock-management",
        icon: "inventory_2",
        permission: "business.stock.view",
        children: [
          {
            id: "stock_adjustment",
            path: "stock-adjustment",
            icon: "tune",
            element: <StockAdjustmentPage />,
            permission: "business.stock.adjustment.view",
          },
          {
            id: "stock_transfers",
            path: "stock-transfers",
            icon: "local_shipping",
            element: <StockTransferPage />,
          },
          {
            id: "stock_movement_logs",
            path: "stock-movement-logs",
            icon: "history",
            element: <StockMovementLogs />,
          },
          {
            id: "stock_valuation",
            path: "stock-valuation",
            icon: "assessment",
            element: <StockValuation />,
          },
          {
            id: "expiry_report",
            path: "expiry-report",
            icon: "schedule",
            element: <ExpiryReport />,
          },
          {
            id: "raw_materials",
            path: "raw-materials",
            icon: "spa",
            permission: "business.ingredients.view",
            children: [
              {
                index: true,
                element: <Ingredients />,
              },
              {
                path: "new",
                element: <IngredientBuilder />,
              },
              {
                path: ":id/edit",
                element: <IngredientBuilder />,
              },
              {
                path: ":id/batches",
                element: <IngredientBatches />,
              },
            ],
          },
          // {
          //   id: "consumables",
          //   path: "consumables",
          //   icon: "category",
          //   permission: "business.consumables.view",
          //   children: [
          //     {
          //       index: true,
          //       element: <Consumables />,
          //     },
          //     {
          //       path: "new",
          //       element: <ConsumableBuilder />,
          //     },
          //     {
          //       path: ":id/edit",
          //       element: <ConsumableBuilder />,
          //     },
          //   ],
          // },
        ],
      },
      {
        id: "kitchen",
        path: "kitchen",
        icon: "kitchen",
        permission: "business.kitchen.view",
        children: [
          {
            id: "recipes",
            path: "recipes",
            icon: "restaurant_menu",
            permission: "business.recipes.view",
            children: [
              {
                index: true,
                element: <Recipes />,
              },
              {
                path: "new",
                element: <RecipeBuilder />,
              },
              {
                path: ":id/edit",
                element: <RecipeBuilder />,
              },
            ],
          },
          {
            id: "processed_items",
            path: "API",
            icon: "lunch_dining",
            permission: "business.processed.items.view",
            children: [
              {
                index: true,
                element: <ProcessedItemsManager />,
              },
            ],
          },
          // {
          //   id: "kitchen_waste",
          //   path: "waste",
          //   icon: "delete_sweep",
          //   permission: "business.kitchen.waste.view",
          //   children: [
          //     {
          //       index: true,
          //       element: <KitchenWaste />,
          //     },
          //   ],
          // },
        ],
      },
      {
        id: "menu",
        path: "menu",
        icon: "restaurant_menu",
        permission: "business.menu.view",
        children: [
          {
            id: "bom",
            path: "bom",
            icon: "list_alt",
            permission: "business.bom.view",
            children: [
              {
                index: true,
                element: <BOMManager />,
              },
              {
                path: "new",
                element: <BOMForm />,
              },
              {
                path: ":id/edit",
                element: <BOMForm />,
              },
            ],
          },
          {
            id: "sale_items",
            path: "sale-items",
            icon: "fastfood",
            permission: "business.sale.items.view",
            children: [
              {
                index: true,
                element: <SaleItems />,
              },
            ],
          },
          {
            id: "menu_items",
            path: "menu-items",
            icon: "menu_book",
            permission: "business.menu.items.view",
            children: [
              {
                index: true,
                element: <MenuItemsManager />,
              },
              {
                path: "new",
                element: <MenuItemForm />,
              },
              {
                path: ":id/edit",
                element: <MenuItemForm />,
              },
            ],
          },
          // {
          //   id: "offers",
          //   path: "offers",
          //   icon: "local_offer",
          //   permission: "business.offers.view",
          //   children: [
          //     {
          //       index: true,
          //       element: <OffersManager />,
          //     },
          //     {
          //       path: "new",
          //       element: <OfferBuilder />,
          //     },
          //     {
          //       path: ":id/edit",
          //       element: <OfferBuilder />,
          //     },
          //   ],
          // },
        ],
      },
      {
        id: "orders",
        path: "orders",
        element: <OrdersManager />,
        icon: "receipt_long",
        permission: "business.orders.view",
      },
      {
        id: "procurement",
        path: "procurement",
        icon: "shopping_cart",
        permission: "business.procurement.view",
        children: [
          {
            id: "purchase-requests",
            path: "purchase-requests",
            icon: "list_alt",
            element: <PurchaseRequests />,
            permission: "business.purchase.requests.view",
          },
          {
            id: "RFQ",
            path: "RFQ",
            icon: "request_quote",
            element: <RFQList />,
            permission: "business.rfq.view",
          },
          {
            id: "purchase-orders",
            path: "purchase-orders",
            icon: "shopping_cart",
            element: <POList />,
            permission: "business.purchase.orders.view",
          },
          {
            id: "grn",
            path: "GRN",
            icon: "inventory",
            permission: "business.grn.view",
            children: [
              {
                index: true,
                element: <GRNList />,
              },
              {
                path: "new",
                element: <GRNFormPage />,
              },
              {
                path: ":id/edit",
                element: <GRNFormPage />,
              },
            ],
          },
          {
            id: "invoices",
            path: "invoices",
            icon: "receipt",
            permission: "business.invoices.view",
            children: [
              {
                index: true,
                element: <InvoiceList />,
              },
              {
                path: "new",
                element: <InvoiceFormPage />,
              },
              {
                path: ":invoiceId/edit",
                element: <InvoiceFormPage />,
              },
              {
                path: ":invoiceId",
                element: <InvoiceFormPage />,
              },
            ],
          },
        ],
      },
      {
        id: "configuration",
        path: "configuration",
        icon: "admin_panel_settings",
        permission: "business.configuration.view",
        children: [
          {
            id: "staff",
            path: "staff",
            element: <StaffManager />,
            icon: "people",
            permission: "business.staff.view",
          },
          {
            path: "staff/new",
            element: <StaffBuilder />,
            handle: { hideSidebar: true },
          },
          {
            path: "staff/:id/edit",
            element: <StaffBuilder />,
            handle: { hideSidebar: true },
          },
          {
            id: "branch",
            path: "branches",
            icon: "store",
            element: <BranchesManager />,
            permission: "business.branches.view",
          },
          {
            id: "warehouse",
            path: "warehouses",
            icon: "warehouse",
            element: <WarehousesManager />,
            permission: "business.warehouses.view",
          },
          {
            id: "department",
            path: "departments",
            icon: "domain",
            element: <DepartmentsManager />,
            permission: "business.departments.view",
          },
          {
            id: "suppliers",
            path: "suppliers",
            icon: "handshake",
            element: <Suppliers />,
            permission: "business.suppliers.view",
          },
          {
            id: "role",
            path: "roles",
            icon: "manage_accounts",
            permission: "business.roles.view",
            children: [
              {
                index: true,
                element: <RolesManager />,
              },
              {
                path: "new",
                element: <RoleBuilder />,
              },
              {
                path: ":id/edit",
                element: <RoleBuilder />,
              },
            ],
          },
          {
            id: "pos_configuration",
            path: "pos-configuration",
            icon: "point_of_sale",
            element: <POSConfiguration />,
            permission: "business.pos.configuration.view",
          },
          {
            id: "tables_configuration",
            path: "tables-configuration",
            icon: "table_restaurant",
            element: <TablesConfiguration />,
            permission: "business.tables.configuration.view",
          },
        ],
      },
      {
        id: "settings",
        path: "settings",
        icon: "settings",
        element: <Settings />,
        permission: "business.settings.view",
      },
    ],
    errorElement: <ErrorBoundary />,
  },
  {
    path: "app-launcher",
    element: (
      <AuthenticatedLayout>
        <BusinessAdminGuard>
          <AppLauncherPage />
        </BusinessAdminGuard>
      </AuthenticatedLayout>
    ),
  },
  {
    path: "profile",
    element: (
      <AuthenticatedLayout>
        <BusinessAdminGuard>
          <ProfilePage />
        </BusinessAdminGuard>
      </AuthenticatedLayout>
    ),
  },
  {
    path: "admin/audit-logs",
    element: (
      <AuthenticatedLayout>
        <BusinessAdminGuard>
          <AuditLogsPage />
        </BusinessAdminGuard>
      </AuthenticatedLayout>
    ),
  },
  {
    path: "admin/permissions",
    element: (
      <AuthenticatedLayout>
        <BusinessAdminGuard>
          <UserManagementPage />
        </BusinessAdminGuard>
      </AuthenticatedLayout>
    ),
  },
];
